import React, { useMemo } from 'react';
import { format, isSameDay } from 'date-fns';
import useSWR from 'swr';

import { Wrap } from '@nearst/ui';
import * as Stats from '@components/StatsCard';
import InsightsWrapper, { getCSVData } from '@components/Graphs/DataWrapper';
import LocalAdsSpend from '@components/Insights/LocalAdsSpend';
import TimeSeriesGraph from '@components/Graphs/TimeSeriesGraph';
import Toggle from '@components/Graphs/Toggle';

import { useInsightsDate } from '@services/InsightsDateProvider';
import { formatPrice } from '@utils/currency';
import { listAllPlans } from '@services/localAds';
import { getDailyAdsSpend } from '@utils/localAdsPlans';
import { currencyFormat, numberFormat, percentageFormat } from '@components/Graphs/formatters';
import calculateCpc from '@utils/calcCpc';

import styles from './LiaPerformanceCard.module.scss';

const InsightsToggleGraph = ({ data, previousData, dailyBudgets, currency }) => {
	// sometimes, our previous period data might be shorter than our current period data
	const offset = previousData.length - data.length;

	return (
		<Toggle
			initialMeasure={{ label: 'Impressions', value: 'impressions' }}
			measures={[
				{ label: 'Impressions', value: 'impressions' },
				{ label: 'Clicks', value: 'clicks' },
				{ label: 'CPC', value: 'cpc' },
				{ label: 'CTR', value: 'ctr' }
			]}
		>
			{(measure) => {
				let formatter = numberFormat;
				if (measure.value === 'ctr') formatter = percentageFormat;
				if (measure.value === 'cpc') formatter = currencyFormat(currency);

				return (
					<div className={styles.maxHeight}>
						<TimeSeriesGraph
							series={data.map((row, index) => ({
								date: row.date,
								value: row[measure.value],
								previousDate: previousData[index + offset]?.date,
								previousValue: previousData[index + offset]?.[measure.value],
								budget: dailyBudgets.find(({ date }) => date === row.date)?.budget,
								currency
							}))}
							formatter={formatter}
							yAxisLabel={measure.label}
							dateAxis
						/>
					</div>
				);
			}}
		</Toggle>
	);
};

const createQuery = (id, dateHelpers) => {
	const { startDate, endDate, comparison, interval } = dateHelpers;
	return [
		{
			table: 'local_ads',
			from: format(startDate, 'yyyy-MM-dd'),
			to: format(endDate, 'yyyy-MM-dd'),
			interval,
			id,
			columns: ['clicks', 'impressions', 'cpc', 'date'],
			sort: [{ column: 'date', order: 'asc' }],
			sum: ['clicks', 'impressions'],
			avg: ['cpc']
		},
		{
			table: 'local_ads',
			from: format(comparison.startDate, 'yyyy-MM-dd'),
			to: format(comparison.endDate, 'yyyy-MM-dd'),
			interval,
			id,
			columns: ['clicks', 'impressions', 'cpc', 'date'],
			sort: [{ column: 'date', order: 'asc' }],
			sum: ['clicks', 'impressions'],
			avg: ['cpc']
		}
	];
};

const LiaPerformanceCard = ({ shops, className }) => {
	const ids = shops.map((shop) => shop.id);

	const { startDate, endDate, comparison, selectedRange, interval } = useInsightsDate();
	const [query, previousPeriodQuery] = createQuery(ids, { startDate, endDate, comparison, interval: interval });

	const { data: allPlans } = useSWR(`local-ads-all-plans`, () => listAllPlans());
	const dailyBudgets = useMemo(() => {
		const plans = allPlans?.filter((plan) => ids.includes(plan.shop) && !isSameDay(new Date(plan.date), new Date(endDate))) || [];
		return getDailyAdsSpend(plans, startDate, endDate, shops);
	}, [allPlans, startDate, endDate, shops, ids]);

	return (
		<Stats.Card className={className}>
			<Stats.Title>Local Ads performance</Stats.Title>
			<Stats.Subtitle>{selectedRange}</Stats.Subtitle>

			<InsightsWrapper query={[query, previousPeriodQuery]} hideError>
				{([data, previousData]) => {
					// Add daily click-through rate
					data = data.map((item) => ({ ...item, ctr: item.impressions ? item.clicks / item.impressions : 0 }));
					previousData = previousData.map((item) => ({ ...item, ctr: item.impressions ? item.clicks / item.impressions : 0 }));

					const csv = getCSVData(data);

					const clicks = data.reduce((acc, cur) => acc + cur.clicks, 0);
					const impressions = data.reduce((acc, cur) => acc + cur.impressions, 0);

					const previousClicks = previousData.reduce((acc, cur) => acc + cur.clicks, 0);
					const previousImpressions = previousData.reduce((acc, cur) => acc + cur.impressions, 0);

					const cpc = calculateCpc(data);
					const ctr = !clicks || !impressions ? 0 : ((clicks / impressions) * 100).toFixed(2);
					const previousCtr =
						!previousClicks || !previousImpressions ? 0 : ((previousClicks / previousImpressions) * 100).toFixed(2);
					const previousCpc = calculateCpc(previousData);

					return (
						<>
							<Stats.Download filename="google-LIA" csv={csv} />
							<Wrap space="1rem" className={styles.stats}>
								<Stats.Block
									description="Impressions"
									currentValue={Number(impressions)}
									previousValue={Number(previousImpressions)}
								/>

								<Stats.Block description="Clicks" currentValue={Number(clicks)} previousValue={Number(previousClicks)} />

								<Stats.Figure>
									{ctr}%
									<Stats.FigureDescription>
										CTR
										<Stats.Tooltip>
											Your click-through rate is a measure of how relevant your products are to the search terms
										</Stats.Tooltip>
										<Stats.PercentageChange previousValue={previousCtr} currentValue={ctr} />
									</Stats.FigureDescription>
								</Stats.Figure>
								<Stats.Figure>
									{formatPrice(cpc, shops[0].billingCurrency)}
									<Stats.FigureDescription>
										CPC
										<Stats.Tooltip>The average you are paying per local ads click from your daily budget</Stats.Tooltip>
										<Stats.PercentageChange previousValue={previousCpc} currentValue={cpc} inverted />
									</Stats.FigureDescription>
								</Stats.Figure>
								<LocalAdsSpend shopIds={ids} />
							</Wrap>
							<InsightsToggleGraph
								data={data}
								previousData={previousData}
								dailyBudgets={dailyBudgets}
								currency={shops[0].billingCurrency}
							/>
						</>
					);
				}}
			</InsightsWrapper>
		</Stats.Card>
	);
};

export default LiaPerformanceCard;
